import React from 'react';
import { Checkbox, Col, Form, Input, Radio, Row, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import WeldMetaBlock from '../WeldMetaBlock';
import { SECTION_HEADER_WIDTH } from '../types';
import type { WeldMeta } from '../types';
import Button from '../../../../../../../../components/common/Button';

const { Text } = Typography;

export interface UtPanelProps {
  form: FormInstance;
  weldKey: string;
  isView: boolean;
  meta?: WeldMeta;
}

/** UT (Ultrasonic Test) detay paneli — JSX PipeInspectionForm'dan birebir taşındı. */
export default function UtPanel({ form, weldKey, isView, meta }: UtPanelProps) {
  return (
    <div style={{ padding: 4, backgroundColor: '#fffbe6', borderRadius: 4 }}>
      {/* Weld meta info from RFI (read-only) */}
      <WeldMetaBlock meta={meta} />
      <Row gutter={[6, 4]}>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Equipment</Text>
            <Form.Item name={['results', weldKey, 'ut', 'equipment']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 200 }} disabled={isView} />
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Probe</Text>
            <Form.Item name={['results', weldKey, 'ut', 'probe_type']} style={{ margin: 0 }}>
              <Radio.Group size="small" disabled={isView}>
                <Radio value="straight_beam">Straight beam</Radio>
                <Radio value="angle_beam">Angle beam</Radio>
              </Radio.Group>
            </Form.Item>
            <Text>Freq (MHz)</Text>
            <Form.Item name={['results', weldKey, 'ut', 'frequency_mhz']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 80 }} disabled={isView} />
            </Form.Item>
            <Text>Angle (°)</Text>
            <Form.Item noStyle dependencies={['results', weldKey, 'ut', 'probe_type']}>
              {({ getFieldValue }) => {
                const isAngle = getFieldValue(['results', weldKey, 'ut', 'probe_type']) === 'angle_beam';
                return (
                  <Form.Item name={['results', weldKey, 'ut', 'probe_angle']} style={{ margin: 0 }}>
                    <Input size="small" style={{ width: 80 }} disabled={isView || !isAngle} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Cal. Block</Text>
            <Form.Item name={['results', weldKey, 'ut', 'calibration_block']} style={{ margin: 0 }}>
              <Radio.Group size="small" disabled={isView}>
                <Radio value="v1">V1</Radio>
                <Radio value="v2">V2</Radio>
                <Radio value="dsc">DSC</Radio>
                <Radio value="custom">Custom</Radio>
              </Radio.Group>
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Couplant</Text>
            <Form.Item name={['results', weldKey, 'ut', 'couplant_type']} style={{ margin: 0 }}>
              <Radio.Group size="small" disabled={isView}>
                <Radio value="water">Water</Radio>
                <Radio value="oil">Oil</Radio>
                <Radio value="gel">Gel</Radio>
                <Radio value="grease">Grease</Radio>
              </Radio.Group>
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Scan</Text>
            <Form.Item name={['results', weldKey, 'ut', 'scan_method']} style={{ margin: 0 }}>
              <Radio.Group size="small" disabled={isView}>
                <Radio value="manual">Manual</Radio>
                <Radio value="automated">Automated</Radio>
                <Radio value="encoded">Encoded</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item name={['results', weldKey, 'ut', 'scan_coverage']} style={{ margin: 0 }}>
              <Radio.Group size="small" disabled={isView}>
                <Radio value="weld_cap">Weld cap</Radio>
                <Radio value="root">Root</Radio>
                <Radio value="haz">HAZ</Radio>
              </Radio.Group>
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Coverage</Text>
            <Form.Item name={['results', weldKey, 'ut', 'coverage_percent']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 120 }} placeholder="% circumference" disabled={isView} />
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Sensitivity</Text>
            <Form.Item name={['results', weldKey, 'ut', 'sensitivity_mode']} style={{ margin: 0 }}>
              <Radio.Group size="small" disabled={isView}>
                <Radio value="dac">DAC</Radio>
                <Radio value="tcg">TCG</Radio>
                <Radio value="dgs">DGS</Radio>
              </Radio.Group>
            </Form.Item>
            <Text>dB</Text>
            <Form.Item name={['results', weldKey, 'ut', 'sensitivity_db']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 80 }} disabled={isView} />
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Range</Text>
            <Form.Item name={['results', weldKey, 'ut', 'range_value']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 120 }} disabled={isView} />
            </Form.Item>
            <Form.Item name={['results', weldKey, 'ut', 'range_unit']} style={{ margin: 0 }} initialValue="mm">
              <Radio.Group size="small" disabled={isView}>
                <Radio value="mm">mm</Radio>
                <Radio value="inch">inch</Radio>
              </Radio.Group>
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Sweep</Text>
            <Form.Item name={['results', weldKey, 'ut', 'sweep_value']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 120 }} disabled={isView} />
            </Form.Item>
            <Form.Item name={['results', weldKey, 'ut', 'sweep_unit']} style={{ margin: 0 }} initialValue="mm">
              <Radio.Group size="small" disabled={isView}>
                <Radio value="mm">mm</Radio>
                <Radio value="inch">inch</Radio>
              </Radio.Group>
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Gate</Text>
            <Text>Start</Text>
            <Form.Item name={['results', weldKey, 'ut', 'gate_start']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 100 }} type="number" placeholder="Start" disabled={isView} />
            </Form.Item>
            <Text>Width</Text>
            <Form.Item name={['results', weldKey, 'ut', 'gate_width']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 100 }} type="number" placeholder="Width" disabled={isView} />
            </Form.Item>
            <Text>Treshold (%) FSH</Text>
            <Form.Item name={['results', weldKey, 'ut', 'gate_threshold_percent']} style={{ margin: 0 }}>
              <Input size="small" style={{ width: 110 }} type="number" min={0} max={100} placeholder="0–100" disabled={isView} />
            </Form.Item>
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Indications</Text>
            <Form.Item name={['results', weldKey, 'ut', 'indications', 'no_cracks']} valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox disabled={isView}>No cracks</Checkbox>
            </Form.Item>
            <Form.Item name={['results', weldKey, 'ut', 'indications', 'proper_fusion']} valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox disabled={isView}>Proper fusion</Checkbox>
            </Form.Item>
            <Form.Item name={['results', weldKey, 'ut', 'indications', 'no_porosity']} valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox disabled={isView}>No porosity</Checkbox>
            </Form.Item>
            <Form.Item name={['results', weldKey, 'ut', 'indications', 'no_laps']} valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox disabled={isView}>No laps</Checkbox>
            </Form.Item>
            {!isView && (
              <Space size={4}>
                <Button size="small" onClick={() => {
                  form.setFieldsValue({ results: { [weldKey]: { ut: { indications: { no_cracks: true, proper_fusion: true, no_porosity: true, no_laps: true } } } } });
                }}>Check all</Button>
                <Button size="small" onClick={() => {
                  form.setFieldsValue({ results: { [weldKey]: { ut: { indications: { no_cracks: false, proper_fusion: false, no_porosity: false, no_laps: false } } } } });
                }}>Uncheck all</Button>
              </Space>
            )}
          </Space>
        </Col>
        <Col xs={24}>
          <Space size={6} align="center" wrap>
            <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Remarks</Text>
            <Form.Item name={['results', weldKey, 'ut', 'remarks']} style={{ margin: 0, flex: 1 }}>
              <Input.TextArea rows={2} style={{ width: 360 }} disabled={isView} />
            </Form.Item>
          </Space>
        </Col>
      </Row>
    </div>
  );
}
